import React, { useEffect, useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { dismissUploadErrors, inspectUploadErrors, REGISTRATION_INCOMPLETE_ERROR } from '../../../services/uploadRegistration';
import { toMillis } from '../../../utils/timestamps';

// Missing members have no card whose Delete button could acknowledge their
// errors. Keep that action at batch level; real failed files retain their cards.
export default function UploadRegistrationErrors({ companyId, tableId = null, profileId = null, tables = [] }) {
  const [candidates, setCandidates] = useState({});
  const [errors, setErrors] = useState([]);
  const [failure, setFailure] = useState('');
  const [busy, setBusy] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [dependencyPaths, setDependencyPaths] = useState([]);
  const [dependencyRevision, setDependencyRevision] = useState(0);
  const tableKey = tables.map(table => table.id).sort().join(',');

  useEffect(() => {
    setCandidates({});
    setErrors([]);
    setDependencyPaths([]);
    if (!companyId) return undefined;
    const tableIds = tableId ? [tableId] : [null, ...tableKey.split(',').filter(Boolean)];
    const unsubscribes = tableIds.map(destination => {
      const scope = destination ? `users/${companyId}/tables/${destination}` : `users/${companyId}`;
      return onSnapshot(query(collection(db, `${scope}/uploads`),
        where('error', '==', REGISTRATION_INCOMPLETE_ERROR)), snapshot => {
        const changes = snapshot.docChanges();
        setCandidates(current => {
          const key = scope;
          const batches = new Map((current[key] || []).map(upload => [upload.id, upload]));
          let changed = false;
          for (const change of changes) {
            const upload = { id: change.doc.id, ...change.doc.data() };
            const relevant = Object.values(upload.raw_manifest || {}).includes('error')
              && (!profileId || upload.direct_profile_id === profileId);
            if (change.type === 'removed' || !relevant) {
              changed = batches.delete(upload.id) || changed;
            } else {
              batches.set(upload.id, upload);
              changed = true;
            }
          }
          // Ordinary upload progress must not re-inspect every historical error.
          return changed ? { ...current, [key]: [...batches.values()] } : current;
        });
      }, error => {
        console.error('Could not watch upload registration errors:', error);
        setFailure('Could not load upload errors.');
      });
    });
    return () => unsubscribes.forEach(unsubscribe => unsubscribe());
  }, [companyId, tableId, profileId, tableKey, attempt]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const found = [];
      const dependencies = new Set();
      // Inspect the workspace copy first so a bucket's mirrored missing member
      // is shown once. Dismissing it clears both existing manifests together.
      const workspaceScope = `users/${companyId}`;
      const scopes = Object.keys(candidates).sort((a, b) => {
        if (a === b) return 0;
        if (a === workspaceScope) return -1;
        if (b === workspaceScope) return 1;
        return a.localeCompare(b);
      });
      const covered = new Set();
      for (const scope of scopes) {
        for (const upload of candidates[scope]) {
          if (cancelled) return;
          const workspace = scope === workspaceScope;
          const options = { companyId, tableId: workspace ? null : scope.split('/').pop(), uploadId: upload.id };
          const result = await inspectUploadErrors(options);
          const ids = [...new Set(result.missing.map(member => member.id))];
          const batchKey = workspace ? upload.id : upload.origin_upload_id || `${scope}/${upload.id}`;
          const unseen = ids.filter(id => !covered.has(`${batchKey}/${id}`));
          if (unseen.length === 0) continue;
          (result.watchPaths || []).forEach(path => dependencies.add(path));
          ids.forEach(id => covered.add(`${batchKey}/${id}`));
          found.push({ options, count: unseen.length, settled: result.settled,
            createdAt: result.createdAt, key: `${scope}/${upload.id}` });
        }
      }
      if (!cancelled) {
        setErrors(found);
        setDependencyPaths([...dependencies].sort());
      }
    })().catch(error => {
      if (!cancelled) {
        console.error('Could not inspect upload errors:', error);
        setFailure('Could not load upload errors.');
      }
    });
    return () => { cancelled = true; };
  }, [companyId, candidates, dependencyRevision]);

  // A workspace error can outlive healthy child batches, whose documents have
  // no error marker. Watch only the dependencies of the displayed notices.
  const dependencyKey = JSON.stringify(dependencyPaths);
  useEffect(() => {
    let active = true;
    const paths = JSON.parse(dependencyKey).filter(path => path.startsWith(`users/${companyId}/`));
    const unsubscribes = paths.map(path => onSnapshot(doc(db, path), () => {
      if (active) setDependencyRevision(revision => revision + 1);
    }, error => {
      if (active) {
        console.error('Could not watch upload completion:', error);
        setFailure('Could not watch the remaining files in this upload.');
      }
    }));
    return () => {
      active = false;
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
  }, [companyId, dependencyKey, attempt]);

  const dismiss = async (entry) => {
    if (busy) return;
    setBusy(entry.key);
    setFailure('');
    try {
      await dismissUploadErrors(entry.options);
      setErrors(current => current.filter(item => item.key !== entry.key));
    } catch (error) {
      setFailure(error.message || 'Could not dismiss upload errors.');
    } finally {
      setBusy(null);
    }
  };

  return <Box>
    {failure && <Alert severity="error" action={<Button onClick={() => { setFailure(''); setAttempt(value => value + 1); }}>Retry</Button>}>
      {failure}
    </Alert>}
    {errors.map(entry => <Alert key={entry.key} severity="warning" sx={{ mb: 1 }}
      action={<Button disabled={!entry.settled || Boolean(busy)} onClick={() => dismiss(entry)}>
        {busy === entry.key ? 'Dismissing…' : 'Dismiss upload errors'}
      </Button>}>
      {entry.count} file{entry.count === 1 ? '' : 's'} in the upload from{' '}
      {new Date(toMillis(entry.createdAt)).toLocaleString()} did not finish uploading.
      {' '}Add those files again. This batch’s automations are withheld.
      {!entry.settled && ' You can dismiss this notice once its registered files have finished.'}
    </Alert>)}
  </Box>;
}
